import { useState } from "react";
import { Button } from "@/components/Base/Button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/Base/Dialog";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/Base/Form";
import { Input } from "@/components/Base/Input";
import { Icon } from "@/components/Common/Icon";
import { Integrators, IntegratorsLabel } from "@/types/v2";

// Define the form schema using zod
const formSchema = z.object({
  label: z.string().min(1, { message: "Label is required" }),
});

type FormValues = z.infer<typeof formSchema>;

export interface EditCustomLabelDialogProps {
  label: string;
  onSave?: (oldLabel: string, newLabel: string) => void;
  onDelete?: () => void;
  onCancel?: () => void;
  integration?: Integrators;
  isSynced?: boolean; // Whether this label matches a live entry in the shop's label catalog
}

export const EditCustomLabelDialog = ({
  label,
  onSave,
  onDelete,
  onCancel,
  integration,
  isSynced,
}: EditCustomLabelDialogProps) => {
  const [open, setOpen] = useState(false);

  // Initialize form with react-hook-form and zod validation
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      label: label,
    },
  });

  const handleSubmit = (values: FormValues) => {
    if (onSave) {
      onSave(label, values.label);
    }
    setOpen(false);
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete();
    }
    setOpen(false);
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    form.reset({
      label: label,
    });
    setOpen(false);
  };

  const nonTekmetricLocked =
    !!integration && integration !== Integrators.tekmetric;
  const locked = isSynced || nonTekmetricLocked;

  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="ml-2"
          onClick={() => {
            setOpen(true);
          }}
        >
          <Icon name={"editSquare"} className={"text-primary-foreground"} />
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[350px] p-0">
        <div className="flex h-[60px] w-full items-center justify-center rounded-sm bg-primary">
          <DialogTitle className="text-white">
            {form.watch("label")}
          </DialogTitle>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <div className="px-5 py-2">
              <div className="text-sm text-accent">
                {isSynced
                  ? `This label is synced from ${
                      integration
                        ? IntegratorsLabel[integration]
                        : "your shop management system"
                    }'s label catalog and can't be renamed or removed here.`
                  : "Edit and remove labels. Note that this does not sync back to your SMS. This does not update linked containers. You will need to manually update any linked containers."}
              </div>
              <FormField
                control={form.control}
                name="label"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="labelName">Label Name</FormLabel>
                    <FormControl>
                      <Input
                        disabled={locked}
                        id="labelName"
                        type="text"
                        placeholder="Enter label name"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex flex-col items-center space-y-3 px-5 py-4">
              <Button
                type="submit"
                variant="accent"
                className="h-[40px] w-full"
                disabled={locked}
              >
                <Icon name={"save"} size={18} />
                Save
              </Button>
              <Button
                type="button"
                variant="destructive"
                className="h-[40px] w-full gap-2"
                onClick={handleDelete}
                disabled={isSynced}
              >
                <Icon name={"delete"} size={18} />
                Delete Label
              </Button>
              <Button
                type="button"
                variant="secondary"
                className="w-full text-sm"
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
